import { useTranslation } from "react-i18next";
import { DerivedMemberType, type MemberInfo } from "@/types/member";
import {
  IconSearch,
  IconCirclePlusFilled,
  IconChevronLeft,
  IconInfoCircle,
  IconDownload,
  IconX,
} from "@tabler/icons-react";
import React, { useState, useCallback } from "react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { useBackNavigation } from "@/hooks/useBackNavigation";
import styles from "./MHeader.module.css";
import { getMemberNickFirst } from "@/utils/member";
import MProfilePicker from "@/components/ui/atoms/MProfilePicker";
import { useAndroidKeyboardDetection } from "@/hooks/useAndroidKeyboardDetection";
import MProgressBar from "@/components/ui/atoms/MProgressBar";

export interface MHeaderProps {
  className?: string;
  module: string;
  memberInfo: MemberInfo;
  headerLabel?: string;
  subHeading?: string;
  title?: React.ReactNode;
  description?: string | React.ReactNode;
  showProfilePickerRow?: boolean;
  hidePetsInPicker?: boolean;
  /**
   * Content to display in the learn more drawer.
   * If provided, a "Learn More" link will be shown after the description that opens a drawer with this content.
   */
  learnMoreContent?: React.ReactNode;
  /** Custom handler for learn more action. If not provided, a default drawer will be shown. */
  onLearnMore?: () => void;
  /** Handler for the add button click */
  onAdd?: () => void;
  /** Hide the learn more link */
  hideLearnMore?: boolean;
  /** Title for the learn more drawer */
  drawerTitle?: string;
  showSearch?: boolean;
  onSearch?: (value: string) => void;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Show gradient background */
  showGradient?: boolean;
  /** Use parent gradient - makes header transparent to inherit parent's gradient */
  useParentGradient?: boolean;
  /** Controlled search input value */
  value?: string;
  /** Controlled search input change */
  onValueChange?: (value: string) => void;
  /** Called when search input is cleared */
  onClear?: () => void;
  /** Active-filter summary badge rendered inside the search box, e.g. "2 filters on ✕".
   *  Omit (or count: 0) to render the search box exactly as before. */
  searchFilterBadge?: { count: number; onClear: () => void };
  /** Disable sticky positioning */
  disableSticky?: boolean;
  /** Hide the back button in subheading row */
  hideBack?: boolean;
  /** Custom back handler - if provided, will be called instead of default back navigation */
  onBack?: () => void;
  /** Show back button for headerLabel (minimal, no subheading structure) */
  showBackButton?: boolean;
  /** Show progress bar with completion text */
  showProgress?: boolean;
  /** Progress value (0-100) */
  progress?: number;
  /** Completed count for progress text */
  completedCount?: number;
  /** Total count for progress text */
  totalCount?: number;
  /** Variant for special header layouts */
  variant?: "default" | "health-summary";
  /** Meta information to display below header title (for health-summary variant) */
  metaInfo?: string;
  /** Handler for info icon click (for health-summary variant) */
  onInfo?: () => void;
  /** Handler for download icon click (for health-summary variant) */
  onDownload?: () => void;
  /** Whether to render the subHeading text (default true). Set false to hide
   *  the title while keeping the row layout — useful when only the Back
   *  control is desired. */
  showTitle?: boolean;
}

/**
 * MHeader component for page headers with title, description, and optional learn more link
 */
const MHeader = ({
  className,
  module,
  memberInfo,
  headerLabel,
  subHeading,
  title: propTitle,
  description: propDescription,
  learnMoreContent: propLearnMoreContent,
  onLearnMore,
  onAdd,
  hideLearnMore,
  drawerTitle = "Learn More",
  showSearch = false,
  onSearch,
  searchPlaceholder = "Search...",
  showGradient = false,
  useParentGradient = false,
  showProfilePickerRow = false,
  hidePetsInPicker = false,
  value,
  onValueChange,
  onClear,
  searchFilterBadge,
  disableSticky = false,
  hideBack = false,
  onBack,
  showBackButton = false,
  showProgress = false,
  progress = 0,
  completedCount = 0,
  totalCount = 0,
  variant = "default",
  metaInfo,
  onInfo,
  onDownload,
  showTitle = true,
}: MHeaderProps) => {
  const { t } = useTranslation();
  const { handleBack } = useBackNavigation({ onBack });

  // Use memberInfo fields directly
  const derivedType = memberInfo?.derivedMemberType || DerivedMemberType.A;

  // Compute name: nickname preferred, else firstName + lastName
  const name = getMemberNickFirst(memberInfo);

  // Determine i18n keys
  // Assume module is always passed as a string key matching en.json
  const i18nModuleKey = module.toLowerCase();

  // State for managing the drawer visibility
  const [showInfoDrawer, setShowInfoDrawer] = useState(false);

  // Try derivedType-specific header, then fallback to module-level, then props
  let title = propTitle;
  let description = propDescription;
  let learnMoreContent = propLearnMoreContent as React.ReactNode;

  // Helper to check if translation exists (t returns key if missing)
  const getT = (key: string, opts?: any) => {
    const translated = t(key, opts);
    return typeof translated === "string" && translated !== key
      ? translated
      : undefined;
  };

  // Encapsulate i18n fallback logic in a private function
  const getI18nHeader = () => {
    const dtKey =
      derivedType === DerivedMemberType.A && memberInfo.onBehalf
        ? "AO"
        : derivedType;
    const base = `header.${i18nModuleKey}`;
    const dtTitleKey = `${base}.${dtKey}.title`;
    const dtDescKey = `${base}.${dtKey}.description`;
    const dtLearnContentKey = `${i18nModuleKey}.learn-more-text`;

    let title = getT(dtTitleKey, { name });
    let description = getT(dtDescKey, { name });
    const learnMoreContent = getT(dtLearnContentKey);

    // Only initialize base keys if dtKey for Title translation is not found
    if (!title) {
      title = getT(`${base}.A.title`, { name }) || "";
      description = getT(`${base}.A.description`, { name }) || "";
    }

    return { title, description, learnMoreContent };
  };

  const i18nHeader = getI18nHeader();
  title = title || i18nHeader.title;
  description = description || i18nHeader.description;
  // Only use learnMoreContent from props or i18n, completely ignore learnMoreLink
  learnMoreContent = learnMoreContent || i18nHeader.learnMoreContent;

  // Use controlled value if provided, else fallback to internal state
  const isControlled =
    typeof value === "string" && typeof onValueChange === "function";
  const [uncontrolledValue, setUncontrolledValue] = useState("");
  const searchValue = isControlled ? value : uncontrolledValue;
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (isControlled) {
      if (onValueChange) onValueChange(val);
    } else {
      setUncontrolledValue(val);
    }
    if (val === "") {
      if (onClear) {
        onClear();
        document.body.classList.remove("search-focused");
      } else {
        document.body.classList.add("search-focused");
      }
    } else {
      document.body.classList.add("search-focused");
    }
  };

  // Android keyboard detection for search input
  const handleAndroidKeyboardClose = useCallback(() => {
    if (document.body.classList.contains("search-focused")) {
      document.body.classList.remove("search-focused");
    }
  }, []);

  useAndroidKeyboardDetection({
    enabled: showSearch,
    onKeyboardClose: handleAndroidKeyboardClose,
  });

  const renderHeaderContent = () => {
    if (subHeading) {
      return (
        <div className={styles.subHeadingRow}>
          <div className={styles.subHeadingLeft}>
            {!hideBack && (
              <button
                type="button"
                className={styles.subHeadingBackBtn}
                onClick={handleBack}
                aria-label="Back"
              >
                <IconChevronLeft
                  size={20}
                  style={{ color: "var(--colour-text-default, #2A2A2A)" }}
                />
                <span className={styles.subHeadingBackLabel}>Back</span>
              </button>
            )}
          </div>
          <div
            className={styles.subHeadingCenter}
            style={{ display: hideBack ? "block" : "flex" }}
          >
            {showTitle && <div className={styles.subHeading}>{subHeading}</div>}
          </div>
          <div className={styles.subHeadingRight} />
        </div>
      );
    }

    if (headerLabel) {
      if (variant === "health-summary") {
        return (
          <>
            <div className={styles.healthSummaryHeader}>
              <div className={styles.healthSummaryRow}>
                {showBackButton && !hideBack && (
                  <button
                    type="button"
                    className={styles.subHeadingBackBtn}
                    onClick={handleBack}
                    aria-label="Back"
                  >
                    <IconChevronLeft
                      size={20}
                      style={{ color: "var(--colour-text-default, #2A2A2A)" }}
                    />
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "var(--colour-text-default)",
                        lineHeight: "1.2",
                      }}
                    >
                      Back
                    </span>
                  </button>
                )}
                <h1 className={styles.healthSummaryTitle}>{headerLabel}</h1>
                {onInfo && (
                  <button
                    type="button"
                    className={styles.infoButton}
                    onClick={onInfo}
                    aria-label="Info"
                  >
                    <IconInfoCircle size={24} style={{ color: "#3263A3" }} />
                  </button>
                )}
                {onDownload && (
                  <button
                    type="button"
                    className={styles.infoButton}
                    onClick={onDownload}
                    aria-label="Download PDF"
                  >
                    <IconDownload size={24} style={{ color: "#8B3166" }} />
                  </button>
                )}
              </div>
              {metaInfo && <div className={styles.metaInfo}>{metaInfo}</div>}
            </div>
          </>
        );
      } else {
        return (
          <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
            <div style={{ flex: "0 0 auto", minWidth: "60px" }}>
              {showBackButton && !hideBack && (
                <button
                  type="button"
                  className={styles.subHeadingBackBtn}
                  onClick={handleBack}
                  aria-label="Back"
                >
                  <IconChevronLeft
                    size={20}
                    style={{ color: "var(--colour-text-default, #2A2A2A)" }}
                  />
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "var(--colour-text-default)",
                      lineHeight: "1.2",
                    }}
                  >
                    Back
                  </span>
                </button>
              )}
            </div>
            <h1 className={styles.headerLabel} style={{ flex: 1 }}>
              {headerLabel}
            </h1>
            <div style={{ flex: "0 0 auto", minWidth: "60px" }} />
          </div>
        );
      }
    }

    if (title) {
      return <h1 className={styles.title}>{title}</h1>;
    }
  };

  return (
    <div
      className={[
        styles.headerContainer,
        useParentGradient ? styles.transparentHeader : "",
        showGradient || className || useParentGradient || disableSticky
          ? ""
          : "white-background",
        className,
        showGradient ? styles.gradientPadding : "",
        i18nModuleKey === "profile" ? styles.profileModule : "",
        disableSticky ? styles.noSticky : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.titleContainer}>
        {/* DEBUG: subHeading={subHeading}, headerLabel={headerLabel}, title={propTitle} */}
        {renderHeaderContent()}
        {!showProfilePickerRow && onAdd && (
          <IconCirclePlusFilled
            className={styles.addIcon}
            onClick={onAdd}
            aria-label="Add new item"
            stroke={1.5}
          />
        )}
      </div>
      {showProfilePickerRow && (
        <div className={styles.belowTitleRow}>
          <div>
            <MProfilePicker borderless hidePets={hidePetsInPicker} />
          </div>

          {onAdd && (
            <IconCirclePlusFilled
              className={styles.addIcon}
              onClick={onAdd}
              aria-label="Add new item"
              stroke={1.5}
            />
          )}
        </div>
      )}
      {showProgress && (
        <>
          <div style={{ marginBottom: "16px", width: "100%" }}>
            <MProgressBar progress={progress} size="large" />
            <div
              style={{
                marginTop: "8px",
                color: "var(--brand-colours-indigo-indigo-700, #36116D)",
                fontFamily: "var(--typography-font-family-base, Manrope)",
                fontSize: "var(--typography-font-size-body-s, 14px)",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "140%",
                letterSpacing: "0.14px",
              }}
            >
              {completedCount} of {totalCount} sections complete
            </div>
          </div>
        </>
      )}
      {showSearch ? (
        <div
          data-search-bar
          className={
            searchFilterBadge && searchFilterBadge.count > 0
              ? `${styles.searchInputContainer} ${styles.searchInputContainerWithBadge}`
              : styles.searchInputContainer
          }
        >
          {searchFilterBadge && searchFilterBadge.count > 0 && (
            <div className={styles.searchFilterBadge}>
              <span className={styles.searchFilterBadgeText}>
                {t("common.filtersOn", { count: searchFilterBadge.count })}
              </span>
              <IconX
                size={12}
                color="#FBFBFB"
                className={styles.searchFilterBadgeClose}
                onClick={(e) => {
                  e.stopPropagation();
                  searchFilterBadge.onClear();
                }}
                aria-label={t("common.clearFilters")}
              />
            </div>
          )}
          <input
            className={styles.searchInput}
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur(); // Blur input to hide keyboard
                document.body.classList.remove("search-focused"); // Show nav and FAB
                if (onSearch) onSearch(searchValue);
              }
            }}
            onFocus={() => {
              document.body.classList.add("search-focused");
            }}
            onBlur={() => {
              document.body.classList.remove("search-focused");
            }}
            placeholder={searchPlaceholder}
            aria-label="Search"
          />
          <IconSearch
            size={16}
            color="#2A2A2A"
            className={styles.searchIcon}
            style={{ cursor: "pointer" }}
            onClick={() => onSearch && onSearch(searchValue)}
            aria-label="Search"
          />
        </div>
      ) : (
        description &&
        description !== "N/A" && (
          <p>
            {typeof description === "string" ? (
              // If string → support \n\n -> paragraphs + inline HTML tags
              <span
                className="body-base-medium"
                dangerouslySetInnerHTML={{
                  __html: description
                    .split("\n\n")
                    .map((p) => `${p}`)
                    .join(""),
                }}
              />
            ) : (
              // If React node (e.g. <> with <br />) → render directly
              <span className="body-base-medium">{description}</span>
            )}

            {!hideLearnMore && learnMoreContent && (
              <>
                {" "}
                <button
                  className="body-base-bold"
                  style={{ fontWeight: 700, fontSize: 16, color: "#B23F83" }}
                  onClick={onLearnMore || (() => setShowInfoDrawer(true))}
                  aria-label="Learn more"
                  type="button"
                >
                  Learn More
                </button>
              </>
            )}
          </p>
        )
      )}
      {learnMoreContent && !showSearch && (
        <FooterDrawer
          open={showInfoDrawer}
          onClose={() => setShowInfoDrawer(false)}
          title={drawerTitle}
        >
          <div className={styles.learnMoreContent}>
            {typeof learnMoreContent === "string" ? (
              <div>
                {learnMoreContent.split("\n\n").map((paragraph, i) => (
                  <p key={i} className="mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              learnMoreContent
            )}
          </div>
        </FooterDrawer>
      )}
    </div>
  );
};

export default MHeader;
